import dayjs from 'dayjs';
import { Timestamp } from 'firebase/firestore';

import { firebaseAuth } from '@client/services/firebase.config';
import { EErrorType, type IMetadata, type TUnixDate } from '@common/types';

import type { User } from 'firebase/auth';

/**
 * Cleans an object by replacing undefined values with null and recursively processing nested objects.
 * Arrays are not modified.
 * @param {object} obj - The object to clean.
 * @param {WeakSet} visited - WeakSet to track visited objects to prevent circular references.
 * @returns {object} The cleaned object.
 */
export function cleanData<T>(obj: T, visited: WeakSet<any> = new WeakSet()): T {
  if (Array.isArray(obj)) {
    // Don't touch array contents
    return obj;
  }

  if (obj !== null && typeof obj === 'object') {
    // Prevent circular references
    if (visited.has(obj as object)) {
      return obj;
    }

    // Skip special object types that should not be recursively processed
    if (
      obj instanceof Timestamp ||
      obj instanceof Date ||
      dayjs.isDayjs(obj) ||
      (obj as any).constructor?.name === 'DocumentReference' ||
      (obj as any).constructor?.name === 'Timestamp'
    ) {
      return obj;
    }

    visited.add(obj as object);

    const result: Record<string, any> = {};

    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];

        if (value === undefined) {
          result[key] = null;
        } else if (value === null) {
          result[key] = null;
        } else if (typeof value === 'object' && !Array.isArray(value)) {
          result[key] = cleanData(value, visited);
        } else {
          result[key] = value;
        }
      }
    }

    return result as T;
  }

  return obj;
}

export const dayjsToTimestamp = (
  date: TUnixDate | dayjs.Dayjs,
): Timestamp | null => {
  if (
    typeof (date as TUnixDate)?.seconds === 'number' &&
    typeof (date as TUnixDate)?.nanoseconds === 'number'
  ) {
    const dayjsDate = dayjsFromTimestamp(date as TUnixDate);
    return dayjsDate ? Timestamp.fromDate(dayjsDate.toDate()) : null;
  }
  return date ? Timestamp.fromDate((date as dayjs.Dayjs).toDate()) : null;
};

export const dayjsFromTimestamp = (
  timestamp: Timestamp | TUnixDate,
  prefix: string = '',
): dayjs.Dayjs | null => {
  let ts = timestamp;
  if (ts instanceof Timestamp) {
    return dayjs(ts.toDate());
  } else if (
    typeof timestamp?.[prefix + 'seconds'] === 'number' &&
    typeof timestamp?.[prefix + 'nanoseconds'] === 'number'
  ) {
    ts = new Timestamp(
      timestamp[prefix + 'seconds'],
      timestamp[prefix + 'nanoseconds'],
    );
    return dayjs((ts as Timestamp).toDate());
  }

  return null;
};

export const createMetadata = (user: User): IMetadata => ({
  createdBy: { uid: user.uid, name: user.displayName as string },
  updatedBy: { uid: user.uid, name: user.displayName as string },
  createdAt: Timestamp.fromDate(new Date()),
  updatedAt: Timestamp.fromDate(new Date()),
});

export const updateMetadata = (user: User): Partial<IMetadata> => ({
  updatedBy: { uid: user.uid, name: user.displayName as string },
  updatedAt: Timestamp.fromDate(new Date()),
});

export const getAuthUser = () => {
  const user = firebaseAuth?.currentUser;
  if (!user) throw new Error(EErrorType.NOT_AUTHENTICATED);
  return user;
};

export const prepareMetadataUpdate = <T>(
  entity: T & { metadata: IMetadata },
) => {
  const user = getAuthUser();

  return {
    metadata: {
      ...entity.metadata,
      ...updateMetadata(user as User),
    },
  };
};
